"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CONVERSATION_ALL_GET,
  CONVERSATION_GET,
  deleteConversation,
  sendLog,
  updateConversation,
} from "@/api";
import {
  AdvisorCoachingResponse,
  Client,
  Conversation,
  ConversationDataTableRowEntity,
  DataExtractionStatus,
  LogLevel,
  Participant,
  SuggestedParticipants,
  TranscriptSegment,
  User,
} from "@/types";
import { useConversationStore, useCrmTaskStore } from "@/store";
import { usePageStore } from "@/store/page";
import { useRouter } from "next/navigation";
import { format } from "date-fns/format";
import { ANALYTICS_EVENTS, DATE_FORMAT } from "@/constants";
import { useTaskManagerStore } from "@/store/taskManager";
import { toast } from "sonner";
import { showErrorToast } from "@/lib/errorUtils";
import { UpdateParticipantData } from "@/components/pages/Conversations/ConversationsDetails/ConversationPersonTag/ConversationPersonPopoverPaginated";
import { AxiosError } from "axios";
import { useAnalytics } from "@/lib";
import { useProcessingStepFlags } from "./conversationDetails/useProcessingStepFlags";
import { useConversationWebSocketSubscriptions } from "./conversationDetails/useConversationWebSocketSubscriptions";
import { useConversationSegmentMutation } from "./conversationDetails/useConversationSegmentMutation";
import { useConversationParticipantMutations } from "./conversationDetails/useConversationParticipantMutations";
import { useConversationQueries } from "./conversationDetails/useConversationQueries";

interface ConversationDetailsContextType {
  isAssignmentModalOpen: boolean;
  openAssignmentModal: () => void;
  closeAssignmentModal: () => void;
  isSuccess: boolean;
  conversation?: Conversation;
  updateMessage: (data: Partial<Conversation>) => void;
  updateSummary: (data: Partial<Conversation>) => void;
  isSummarySavePending: boolean;
  isSummarySaveError: boolean;
  onDeleteConversation: (id: string) => void;
  onSelectParticipant: (data: UpdateParticipantData) => void;
  /** Unlink mapped cards first; otherwise request deletion. Null closes the prompt. */
  setParticipantToRemove: (id: string | null) => void;
  participantToRemove: string | null;
  onDeleteParticipant: (id: string) => void;
  isPending: boolean;
  participants: Participant[];
  isParticipantsPending: boolean;
  createOrUpdateParticipant: (data: Partial<Participant>) => void;
  createParticipantAsync: (
    data: Partial<Participant>,
  ) => Promise<Participant | undefined>;
  updateParticipantAsync: (
    data: Partial<Participant>,
  ) => Promise<Participant | undefined>;
  hasNoAccess: boolean;
  reassignSpeaker: (participantId: string, speakerId: string) => Promise<void>;
  isSpeakerUpdating: boolean;
  transcriptMessages?: TranscriptSegment[];
  conversationId: string;
  updateSegment: (data: { segmentId: string; newSpeakerId: string }) => void;
  segmentUpdating?: string;
  selectReassign?: SelectToReassign | null;
  setSelectReassign: (value: SelectToReassign | null) => void;
  isTranscriptMessagesPending: boolean;
  isSpeakerUpdated: boolean;
  closeSpeakerUpdateWarning: () => void;
  markSpeakerUpdated: () => void;
  participantUpdatingId?: string;
  coachingData?: AdvisorCoachingResponse;
  isCoachingLoading: boolean;
  isCoachingDisabled: boolean;
  isDataEnabled: boolean;
  hasCoachingError: boolean;
  hasUnassignedSpeakers: boolean;
  suggestedParticipants?: SuggestedParticipants;
  dataExtractionStatus?: DataExtractionStatus | null;
  dataTableRows?: ConversationDataTableRowEntity[];
  chatId?: string;
  isChatInitializing: boolean;
  // Processing step computed values
  isConversationPageReady: boolean;
  isConversationFatalError: boolean;
  isSummaryStepLoading: boolean;
  isSummaryStepErrored: boolean;
  summaryErrorReason: string | null;
  isCoachingStepErrored: boolean;
  coachingStepErrorReason: string | null;
  isDataExtractionStepErrored: boolean;
  dataExtractionStepErrorReason: string | null;
}

const ConversationDetailsContext = createContext<
  ConversationDetailsContextType | undefined
>(undefined);

interface ConversationDetailsProviderProps {
  children: ReactNode;
  conversationId: string;
}

interface SelectToReassign {
  segment: TranscriptSegment;
  participantId: string;
  speakerId: string;
  speakerToSwitchId?: string | "unknown";
  createData?: {
    data: Client | User;
    type: "client" | "user";
  };
}

export const ConversationDetailsProvider: React.FC<
  ConversationDetailsProviderProps
> = ({ children, conversationId }) => {
  const [participantToRemove, setParticipantToRemove] = useState<string | null>(
    null,
  );
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const clearTranscriptHighlight = useConversationStore(
    (s) => s.clearTranscriptHighlight,
  );
  const openAssignmentModal = React.useCallback(() => {
    clearTranscriptHighlight();
    setIsAssignmentModalOpen(true);
  }, [clearTranscriptHighlight]);
  const closeAssignmentModal = React.useCallback(() => {
    setIsAssignmentModalOpen(false);
  }, []);
  const { setTitle } = usePageStore();
  const navigation = useRouter();
  const { setTaskManagerOption } = useTaskManagerStore();
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [wasDeleted, setWasDeleted] = useState(false);
  // Mirrors the websocket-connected state from
  // useConversationWebSocketSubscriptions so it can be passed into
  // useConversationQueries (which is called *before* the websockets hook
  // because the websockets hook depends on linkedClientIds derived from
  // conversation). The queries hook syncs this prop into an internal ref so
  // its refetchInterval callbacks observe the up-to-date value on each tick.
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  const queries = useConversationQueries({
    conversationId,
    hasNoAccess,
    isWebSocketConnected,
  });
  const {
    conversation,
    isSuccess,
    conversationError,
    transcriptMessages,
    isTranscriptMessagesPending,
    suggestedParticipants,
    coachingData,
    isCoachingLoading,
    hasCoachingError,
    isCoachingDisabled,
    isDataEnabled,
    dataExtractionData,
    dataTableRows,
    participants: participantsFromQuery,
    isParticipantsPending,
    isChatInitializing,
  } = queries;

  const effectiveParticipants: Participant[] = useMemo(
    () =>
      (isParticipantsPending
        ? conversation?.participants
        : participantsFromQuery) ?? [],
    [participantsFromQuery, conversation?.participants, isParticipantsPending],
  );

  const linkedClientIds = useMemo(
    () =>
      new Set(
        effectiveParticipants
          .map((participant) => participant.clientId)
          .filter((clientId): clientId is string => Boolean(clientId)),
      ),
    [effectiveParticipants],
  );

  const { isWebSocketConnected: isWebSocketConnectedLive } =
    useConversationWebSocketSubscriptions({
      conversationId,
      hasNoAccess,
      queryClient,
      linkedClientIds,
    });

  // Sync live websocket-connected state into local state so it propagates
  // into useConversationQueries on the next render.
  useEffect(() => {
    setIsWebSocketConnected(isWebSocketConnectedLive);
  }, [isWebSocketConnectedLive]);

  useEffect(() => {
    if (conversationError) {
      const error = conversationError as AxiosError;
      if (
        error.response?.status &&
        [400, 401, 403, 404].includes(error.response?.status)
      ) {
        setHasNoAccess(true);
      }
    }
  }, [conversationError]);

  const { mutate: updateMessage, isPending: isUpdateMessagePending } =
    useMutation({
      mutationFn: updateConversation,
      onSuccess: async (data) => {
        const queryKey = [CONVERSATION_GET, conversation?.id];
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData<Conversation | undefined>(queryKey, (prev) =>
          prev ? { ...prev, ...data } : prev,
        );
        queryClient.invalidateQueries({ queryKey });
      },
      retry: false,
    });

  // Separate mutation for the summary editor's auto-save so its pending /
  // error state is not contaminated by the generic updateMessage above
  // (which is also used for title bootstrap and inline header edits).
  const {
    mutate: updateSummary,
    isPending: isSummarySavePending,
    isError: isSummarySaveError,
  } = useMutation({
    mutationFn: updateConversation,
    onSuccess: async (data) => {
      const queryKey = [CONVERSATION_GET, conversation?.id];
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<Conversation | undefined>(queryKey, (prev) =>
        prev ? { ...prev, ...data } : prev,
      );
      queryClient.invalidateQueries({ queryKey });
    },
    retry: false,
  });

  const { mutate: onDeleteConversation, isPending: isDeletePending } =
    useMutation({
      mutationFn: (id: string) => deleteConversation(id),
      onSuccess: () => {
        setWasDeleted(true);
        navigation.push("/conversations");
        queryClient.invalidateQueries({
          queryKey: [CONVERSATION_ALL_GET, "", "my"],
        });
        queryClient.invalidateQueries({
          queryKey: [CONVERSATION_ALL_GET, "", "firm"],
        });
        toast.success("Conversation deleted");
      },
      onError: (error) => {
        showErrorToast(error, "conversation");
      },
    });

  const { setConversationId } = useCrmTaskStore();

  useEffect(() => {
    setConversationId(conversationId);
    if (conversation?.title) {
      setTitle(conversation.title);
    } else if (conversation?.recordedAt) {
      setTitle(`Meeting on ${format(conversation?.recordedAt, DATE_FORMAT)}`);
      updateMessage({ id: conversationId });
    } else {
      setTitle("");
    }
    return () => {
      setTitle("");
    };
  }, [conversation?.title, conversation?.recordedAt, setTitle]);

  useEffect(() => {
    return () => {
      setTaskManagerOption(null);
    };
  }, [conversationId]);

  useEffect(() => {
    track(
      ANALYTICS_EVENTS.VIEWED_CONVERSATION_DETAILS,
      {
        conversationId: conversation?.id,
        conversationTitle: conversation?.title || "",
        conversationRecordedAt: conversation?.recordedAt
          ? format(conversation.recordedAt, DATE_FORMAT)
          : "",
      },
      { includeReferrer: true },
    );
  }, [conversation?.id]);

  const participantMutations = useConversationParticipantMutations({
    conversationId,
    conversation,
    transcriptMessages,
    queryClient,
    participants: effectiveParticipants,
    onRequestParticipantDeletion: setParticipantToRemove,
  });

  const participants = useMemo(() => {
    return [...effectiveParticipants].sort((a, b) => a.id?.localeCompare(b.id));
  }, [effectiveParticipants]);

  // Check if there are any speakers in the transcript that are not assigned to a participant
  const hasUnassignedSpeakers = useMemo(() => {
    if (!transcriptMessages?.length) return false;

    // Get unique speaker IDs from transcript
    const transcriptSpeakerIds = new Set(
      transcriptMessages.map((segment) => segment.speakerId).filter(Boolean),
    );

    // Get speaker IDs that are assigned to participants
    const assignedSpeakerIds = new Set(
      participants.filter((p) => p.speakerId).map((p) => p.speakerId),
    );

    // Check if any transcript speaker is not assigned
    for (const speakerId of transcriptSpeakerIds) {
      if (!assignedSpeakerIds.has(speakerId)) {
        return true;
      }
    }

    return false;
  }, [transcriptMessages, participants]);

  const segmentMutation = useConversationSegmentMutation({
    conversationId,
    queryClient,
    updateParticipantAsync: participantMutations.updateParticipantAsync,
    markSpeakerUpdated: participantMutations.markSpeakerUpdated,
  });

  useEffect(() => {
    if (effectiveParticipants.length) {
      effectiveParticipants.forEach((participant) => {
        if (participant.clientId && participant.userId) {
          sendLog({
            level: LogLevel.WARN,
            message: "Participant has both clientId and userId",
            component: "ConversationDetailsProvider",
            data: {
              participantId: participant.id,
              conversationId: conversation?.id ?? conversationId,
              clientId: participant.clientId,
              userId: participant.userId,
            },
          });
        }
      });
    }
  }, [effectiveParticipants, conversation?.id]);

  // Processing step computed values
  const stepFlags = useProcessingStepFlags(conversation);

  const contextValue: ConversationDetailsContextType = {
    isAssignmentModalOpen,
    openAssignmentModal,
    closeAssignmentModal,
    isSuccess,
    conversation,
    updateMessage,
    updateSummary,
    isSummarySavePending,
    isSummarySaveError,
    onDeleteConversation,
    setParticipantToRemove: participantMutations.requestParticipantRemoval,
    participantToRemove,
    isPending:
      isUpdateMessagePending ||
      isSummarySavePending ||
      isDeletePending ||
      wasDeleted,
    participants,
    isParticipantsPending,
    ...participantMutations,
    hasNoAccess,
    transcriptMessages,
    conversationId,
    ...segmentMutation,
    isTranscriptMessagesPending,
    coachingData: coachingData ?? undefined,
    isCoachingLoading,
    isCoachingDisabled,
    isDataEnabled,
    hasCoachingError,
    hasUnassignedSpeakers,
    suggestedParticipants,
    dataExtractionStatus: dataExtractionData?.status,
    dataTableRows,
    chatId: undefined,
    isChatInitializing,
    ...stepFlags,
  };

  return (
    <ConversationDetailsContext.Provider value={contextValue}>
      {children}
    </ConversationDetailsContext.Provider>
  );
};

export const useConversationDetailsContext =
  (): ConversationDetailsContextType => {
    const context = useContext(ConversationDetailsContext);
    if (context === undefined) {
      throw new Error(
        "useConversationDetails must be used within a ConversationDetailsProvider",
      );
    }
    return context;
  };

export { ConversationDetailsContext };
